import React, { memo, useMemo } from 'react';
import { Icon, IconsEnum } from 'components/Icons';
import { Leaf, ScommesseLeafItem, ScommesseLeafItemType } from 'components/scommesseTipo/components/cells';
import { useCartToggleHandler, useIsInCart } from 'features/carrello/hooks';
import { useEsito, useInfoEsito, useIsInfoAggActive } from 'features/sport/hooks';

import { DynamicImage, enLoading } from 'components/Image';
import { SkeletonCell } from 'components/scommesseTipo/components/cells/SkeletonCell';
import classNames from 'classnames';
import { isNotEmpty } from 'utility/functions';
import styles from './ScommesseEsitoTipoCard.module.scss';
import { useQuotaVariation } from 'hooks';
import { useTranslation } from 'hooks/useLingUI';

export type ScommesseEsitoTipoCardProps = {
  type?: ScommesseLeafItemType;
  keyEsito: string;
  isBalanced?: boolean;
  scommessaKey?: string;
  backgroundType?: 'primary' | 'secondary';
  additionalText?: string;
  infoAggiuntivaKey?: string;
  gtagAction?: () => void;
};

export const ScommesseEsitoTipoCard = memo(function ScommesseEsitoTipoCard({
  type = ScommesseLeafItem.Esito,
  keyEsito,
  isBalanced,
  scommessaKey,
  backgroundType,
  infoAggiuntivaKey,
  gtagAction,
}: ScommesseEsitoTipoCardProps) {
  const { t } = useTranslation();
  const isActiveInCart = useIsInCart(keyEsito);

  const maybeEsito = useEsito(keyEsito);
  const { quota, isActive: isEsitoActive, infoEsitoKey, isVisible } = maybeEsito ?? {};

  const maybeInfoEsito = useInfoEsito(infoEsitoKey);
  const { iconSrc, descrizione, descrizioneTrKey } = maybeInfoEsito ?? {};

  const { useIsActiveHook, isActiveKey } = useMemo(() => {
    return {
      isActiveKey: infoAggiuntivaKey,
      useIsActiveHook: useIsInfoAggActive,
    };
  }, [infoAggiuntivaKey]);

  const isInfoAggActive = useIsActiveHook(isActiveKey); // useIsInfoAggActive(infoAggiuntivaKey);

  const [isArrowVisible, isIncreasing, isDecreasing] = useQuotaVariation(maybeEsito);

  const onCartToggleHandler = useCartToggleHandler(keyEsito);

  const quotaValue = useMemo(() => (isInfoAggActive && isVisible ? quota : '-'), [isInfoAggActive, isVisible, quota]);

  if (!maybeEsito) {
    return (
      <SkeletonCell
        key={keyEsito}
        type={ScommesseLeafItem.Esito}
        itemKey={keyEsito}
        scommessaKey={`${scommessaKey}`}
        infoAggiuntivaKey={`${infoAggiuntivaKey}`}
        header={descrizione}
        headerTrKey={descrizioneTrKey}
      />
    );
    // return <ScommesseEsitoSkeleton text="" testId={keyEsito} />;
  }

  return (
    <button
      disabled={!isEsitoActive || !isInfoAggActive}
      type="button"
      className={classNames(styles.buttonCard, styles[`background_${backgroundType}`], {
        [styles.buttonCardIsActive]: isActiveInCart,
        [styles.hidden]: !isEsitoActive,
      })}
      data-testid={keyEsito}
      data-kind={'betting-option'}
      onClick={() => {
        gtagAction && gtagAction();
        onCartToggleHandler();
      }}
    >
      <div className={styles.wrapper}>
        {isNotEmpty(iconSrc) && (
          <div className={styles.imageContainer}>
            <DynamicImage
              src={`${iconSrc}`}
              alt={t(descrizioneTrKey, descrizione) ?? ''}
              fill
              loading={enLoading.lazy}
              decoding="async"
              className={styles.image}
            />
          </div>
        )}
        {isBalanced && <span className={styles.balanced}></span>}
        <span className={styles.text}>
          <Leaf
            type={type}
            itemKey={type === ScommesseLeafItem.InfoAggiuntivaWithEsito ? infoAggiuntivaKey! : keyEsito!}
            scommessaKey={scommessaKey!}
            infoAggiuntivaKey={infoAggiuntivaKey!}
          />
        </span>
      </div>
      <p className={styles.quota}>
        <span
          className={classNames(styles.quotaWrapper, {
            [styles.changed]: isArrowVisible,
            [styles.increasing]: isIncreasing,
            [styles.decreasing]: isDecreasing,
          })}
        >
          {quotaValue}
        </span>
      </p>
    </button>
  );
});